import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Table, Input as AntInput, Button as AntButton, Dropdown, Checkbox, Space, Drawer, Switch, message, DatePicker } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getOrderStatusList, getOrderStatusListByProductNo, getGroupProfitDetail, getGroupEventInfo, saveGroupDepartureConfirm, saveGroupScheduleConfirm, getCities, getManagers, getProductTypes } from '../services/apiService';
import { getProductDetails, saveProductBasicInfo, getHotelBasicInfo } from '../services/productService';
import { getUserInfo } from '../utils/tokenManager';
import ProductSettingsModal from '../components/ProductSettingsModal';
import ProductSwitcherModal from '../components/ProductSwitcherModal';
import GroupInfoDrawer from '../components/GroupInfoDrawer';
import AvailableDatesModal from '../components/AvailableDatesModal';
import HotelReservationModal from '../components/HotelReservationModal';
import TourReservationModal from '../components/TourReservationModal';
import ProductPageHeader from '../components/shared/ProductPageHeader';
 
  const columns = [
  { key: '주문번호', label: '주문번호', type: 'string' },
  { key: '행사시작일', label: '행사시작일', type: 'date' },
  { key: '행사종료일시', label: '행사종료일', type: 'date' },
  { key: '예약일', label: '예약일', type: 'date' },
  { key: '예약상태2', label: '예약상태2', type: 'string' },
  { key: '여행객수', label: '여행객수', type: 'number' },
  { key: '예약고객명', label: '주문 고객명', type: 'string' },
  { key: '예약상태3', label: '예약상태3', type: 'string' },
  { key: '상품금액', label: '상품금액', type: 'number' },
  { key: '입금액', label: '입금액', type: 'number' },
  { key: '입금상태', label: '입금상태', type: 'string' },
  { key: '판매처', label: '판매처', type: 'string' },
];

// Format numeric-like values with comma separators (e.g., 1234567 -> 1,234,567)
function formatNumberWithCommas(value) {
  if (value === null || value === undefined || value === '') return '';
  // Strip existing commas just in case, then convert to number
  const num = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('ko-KR');
}

// Decide formatting based on column key
function formatCellValue(key, value) {
  if (key === '상품금액' || key === '입금액') {
    return formatNumberWithCommas(value);
  }
  if (key === '행사종료일시') {
    if (value === null || value === undefined || value === '') return '';
    const s = String(value).trim();
    // Common patterns: "YYYY-MM-DD HH:MM[:SS]", "YYYY-MM-DDTHH:MM[:SS]", keep only date.
    if (s.includes(' ')) return s.split(' ')[0];
    if (s.includes('T')) return s.split('T')[0];
    const m = s.match(/^\d{4}[./-]\d{1,2}[./-]\d{1,2}/);
    if (m) return m[0];
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return s;
  }
  return value ?? '';
}

export default function OrderListPage({ afterHeader, onCreateProduct, creatingProduct, headerInlineRight, hideHeader = false, hideReservationList = false, onOpenReservationList }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { productCode: encodedProductCode } = useParams();
  const productCode = decodeURIComponent(encodedProductCode || '');

  const queryProductNo = useMemo(() => {
    const navProductNo = location?.state?.productNo;
    if (navProductNo != null && String(navProductNo).trim() !== '') return Number(navProductNo);
    const code = (productCode || '').trim();
    if (code && code !== '-' && /^\d+$/.test(code)) return Number(code);
    try {
      const saved = productCode ? sessionStorage.getItem(`order_product_no_${productCode}`) : null;
      if (saved) return Number(saved);
    } catch {}
    return undefined;
  }, [location?.state?.productNo, productCode]);

  const isQuoteDetailView = useMemo(() => {
    return (location?.pathname || '').startsWith('/quote-detail');
  }, [location?.pathname]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  // start/end date filter UI (RangePicker) - prefill default: -1y ~ +1y (과거 주문 포함)
  const [dateRange, setDateRange] = useState(() => [dayjs().subtract(1, 'year'), dayjs().add(1, 'year')]); // [dayjs, dayjs]
  // last effective range used for query (for display)
  const [lastQueryRange, setLastQueryRange] = useState(() => ({
    start: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
    end: dayjs().add(1, 'year').format('YYYY-MM-DD'),
  })); // { start: string, end: string }
  // sorting & filtering state
  const [sortKey, setSortKey] = useState(null); // column key
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [colFilters, setColFilters] = useState({}); // { [key]: string }
  const [globalFilter, setGlobalFilter] = useState(''); // global search across all visible columns
  const [density, setDensity] = useState('compact'); // 'compact' | 'comfortable'
  const [hideCancelled, setHideCancelled] = useState(true);
  // Hide '예약상태3' by default
  const [visibleCols, setVisibleCols] = useState(() => new Set(columns.filter(c => c.key !== '예약상태3').map(c => c.key)));
  const [showColMenu, setShowColMenu] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // Hover state to drive contextual buttons
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  // 드래그 가능한 컬럼 순서 상태 (localStorage 유지)
  const LS_COL_ORDER_KEY = 'orders_col_order_v1';
  const defaultColOrder = useMemo(() => columns.map(c => c.key), []);
  const [colOrder, setColOrder] = useState(() => {
    try {
      const raw = window.localStorage.getItem(LS_COL_ORDER_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (!Array.isArray(saved) || saved.length === 0) return defaultColOrder;
      const setSaved = new Set(saved);
      const merged = saved.filter(k => defaultColOrder.includes(k));
      defaultColOrder.forEach(k => { if (!setSaved.has(k)) merged.push(k); });
      return merged;
    } catch {
      return defaultColOrder;
    }
  });
  const [draggingColKey, setDraggingColKey] = useState(null);
  // Detail drawers state and open handlers
  const [groupDetail, setGroupDetail] = useState(null); // { groupKey, groupName, count, items }
  const [groupProfit, setGroupProfit] = useState(null); // result from TRP_S_예약관리_단체손익
  const [groupProfitLoading, setGroupProfitLoading] = useState(false);
  const [groupProfitError, setGroupProfitError] = useState(null);
  const [groupEvent, setGroupEvent] = useState(null); // result from TRP_S_예약관리_행사단체정보
  const [groupEventLoading, setGroupEventLoading] = useState(false);
  const [groupEventError, setGroupEventError] = useState(null);
  // editable flags (UI only for now)
  const [departureConfirm, setDepartureConfirm] = useState(false);
  const [scheduleConfirm, setScheduleConfirm] = useState(false);
  const [groupEventSaveLoading, setGroupEventSaveLoading] = useState(false);

  const openGroupDetail = async (groupRecord) => {
    if (!groupRecord || !groupRecord._isGroup) return;
    const items = [];
    if (groupRecord._firstChild) items.push(groupRecord._firstChild);
    if (Array.isArray(groupRecord.children)) items.push(...groupRecord.children);
    const first = items[0];
    const saleNo = first?.판매번호 ?? first?.주문판매번호;
    setGroupDetail({
      groupKey: groupRecord.groupKey,
      groupName: groupRecord.groupName,
      count: groupRecord.count,
      items,
      saleNo,
    });
    // fetch group profit detail
    setGroupProfit(null);
    setGroupProfitError(null);
    if (saleNo != null) {
      try {
        setGroupProfitLoading(true);
        const data = await getGroupProfitDetail({ saleNo });
        setGroupProfit(data || null);
      } catch (e) {
        console.error('Failed to fetch group profit detail', e);
        setGroupProfitError(e?.message || '단체 손익 정보를 불러오지 못했습니다.');
      } finally {
        setGroupProfitLoading(false);
      }
    }

    // fetch group event info
    setGroupEvent(null);
    setGroupEventError(null);
    if (saleNo != null) {
      try {
        setGroupEventLoading(true);
        const info = await getGroupEventInfo({ saleNo });
        setGroupEvent(info || null);
        setDepartureConfirm(!!info?.출발확정);
        setScheduleConfirm(!!info?.일정확정);
      } catch (e) {
        console.error('Failed to fetch group event info', e);
        setGroupEventError(e?.message || '단체 정보를 불러오지 못했습니다.');
      } finally {
        setGroupEventLoading(false);
      }
    }
  };

  const openOrderDetail = (row) => {
    if (!row || row._isGroup) return;
    // 해당 주문번호의 예약관리(상세) 화면으로 이동
    const orderNo = row?.주문번호 || row?._groupKey || row?.orderNo;
    if (!orderNo) return;
    const saleNo = row?.판매번호 ?? row?.주문판매번호 ?? null;
    const productName = row?.판매상품명 ?? derivedProductName ?? null;
    navigate(`/reservation-detail/${orderNo}`, { state: { saleNo, productName } });
  };

  // helpers for sorting (placed early to avoid use-before-init in hooks below)
  const getColType = (key) => columns.find((c) => c.key === key)?.type || 'string';
  const parseNumber = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const num = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(num) ? num : null;
  };
  const parseDate = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const s = String(v).trim();
    // Try common splits first
    let dateStr = s;
    if (s.includes(' ')) dateStr = s.split(' ')[0];
    if (dateStr.includes('T')) dateStr = dateStr.split('T')[0];
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  // Determine if a row should be shown as cancelled based on 예약상태2 text
  const isCancelledRow = (record) => {
    if (!record) return false;
    // For group rows, check first child's status
    const target = record._isGroup ? record._firstChild : record;
    const text = (target?.예약상태2 || '').toString();
    return (
      text.includes('취소완료') ||
      text.includes('취소확정') ||
      text.includes('취소 접수') || // safety with space variant
      text.includes('취소접수')
    );
  };
  function compareRows(a, b) {
    if (!sortKey) return 0;
    const type = getColType(sortKey);
    const av = a?.[sortKey];
    const bv = b?.[sortKey];
    let cmp = 0;
    if (type === 'number') {
      const na = parseNumber(av);
      const nb = parseNumber(bv);
      if (na === null && nb === null) cmp = 0; else if (na === null) cmp = -1; else if (nb === null) cmp = 1; else cmp = na - nb;
    } else if (type === 'date') {
      const da = parseDate(av);
      const db = parseDate(bv);
      const ta = da ? da.getTime() : null;
      const tb = db ? db.getTime() : null;
      if (ta === null && tb === null) cmp = 0; else if (ta === null) cmp = -1; else if (tb === null) cmp = 1; else cmp = ta - tb;
    } else {
      const sa = (av ?? '').toString();
      const sb = (bv ?? '').toString();
      cmp = sa.localeCompare(sb, 'ko');
    }
    return sortDir === 'asc' ? cmp : -cmp;
  }
  const toggleSort = (key) => {
    setSortKey((prevKey) => {
      if (prevKey !== key) {
        setSortDir('asc');
        return key;
      }
      // same key, toggle
      setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
      return key;
    });
  };
  const onFilterChange = (key, value) => {
    setColFilters((prev) => ({ ...prev, [key]: value }));
  };
  const visibleColumns = useMemo(() => {
    const filtered = columns.filter(c => visibleCols.has(c.key));
    const idxOf = (k) => { const i = colOrder.indexOf(k); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
    return filtered.sort((a, b) => idxOf(a.key) - idxOf(b.key));
  }, [visibleCols, colOrder]);

  const persistColOrder = (order) => { try { window.localStorage.setItem(LS_COL_ORDER_KEY, JSON.stringify(order)); } catch {} };
  const moveColBefore = (sourceKey, targetKey) => {
    setColOrder(prev => {
      if (!sourceKey || !targetKey || sourceKey === targetKey) return prev;
      const next = prev.slice();
      const sIdx = next.indexOf(sourceKey);
      const tIdx = next.indexOf(targetKey);
      if (sIdx === -1 || tIdx === -1) return prev;
      next.splice(sIdx, 1);
      next.splice(tIdx, 0, sourceKey);
      persistColOrder(next);
      return next;
    });
  };

  // data loader with optional date range
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      if (isQuoteDetailView) {
        if (!queryProductNo) {
          setRows([]);
          return;
        }
      }

      // compute effective start/end (match API defaults when empty)
      let startDate = Array.isArray(dateRange) && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined;
      let endDate = Array.isArray(dateRange) && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined;
      if (!startDate || !endDate) {
        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        const oneYearLater = new Date(today);
        oneYearLater.setFullYear(today.getFullYear() + 1);
        startDate = startDate || toYmd(today);
        endDate = endDate || toYmd(oneYearLater);
      }

      const list = isQuoteDetailView
        ? await getOrderStatusListByProductNo({ startDate, endDate, productNo: queryProductNo })
        : (queryProductNo
          ? await getOrderStatusListByProductNo({ startDate, endDate, productNo: queryProductNo })
          : await getOrderStatusList({ startDate, endDate }));
      setLastQueryRange({ start: startDate, end: endDate });
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Failed to fetch order list', e);
      setError(e?.message || '주문 내역을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isQuoteDetailView && !queryProductNo) return;
    loadData();
  }, [isQuoteDetailView, queryProductNo]);

  // Lock body scroll while this page is mounted so only the table area can scroll
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    
    // Add custom CSS for child rows
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .child-row td:first-child {
        border-left: 4px solid #e5e7eb !important;
      }
      .ant-table-row-expand-icon-cell {
        width: 48px !important;
      }
      .ant-table-row-expand-icon {
        display: none !important;
      }
    `;
    document.head.appendChild(styleEl);
    
    return () => {
      document.body.style.overflow = prevOverflow;
      document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
      document.head.removeChild(styleEl);
    };
  }, []);

  const filtered = useMemo(() => {
    const data = Array.isArray(rows) ? rows : [];
    const code = (productCode || '').trim();
    const navName = (location?.state?.productName || '').trim();
    const navProductNo = location?.state?.productNo;
    // 1) productNo 우선 필터 (명확도가 가장 높음)
    if (navProductNo != null) {
      const noKeys = ['상품번호','ProductNo','productNo','판매상품번호'];
      const byNo = data.filter((r) => noKeys.some((k) => String(r?.[k] ?? '') === String(navProductNo)));
      if (byNo.length) return byNo;
    }
    // 1.5) URL 파라미터가 숫자라면 상품번호로도 시도 (경로 파라미터로 번호를 넘기는 경우 지원)
    if (code && /^\d+$/.test(code)) {
      const asNo = code;
      const noKeys = ['상품번호','ProductNo','productNo','판매상품번호'];
      const byUrlNo = data.filter((r) => noKeys.some((k) => String(r?.[k] ?? '') === String(asNo)));
      if (byUrlNo.length) return byUrlNo;
    }
    // 2) productCode 필터: 다양한 키 시도
    if (code && code !== '-') {
      const codeKeys = ['상품코드','ProductCode','productCode','판매상품코드'];
      const byCode = data.filter((r) => codeKeys.some((k) => String(r?.[k] ?? '').trim() === code));
      if (byCode.length) return byCode;
    }
    // 3) productName fallback: 다양한 키 + 대소문자 무시 비교
    const nameToUse = navName;
    if (nameToUse) {
      const low = nameToUse.toLowerCase();
      const nameKeys = ['상품명','판매상품명','ProductName','productName'];
      const byName = data.filter((r) => nameKeys.some((k) => String(r?.[k] ?? '').trim().toLowerCase() === low));
      if (byName.length) return byName;
    }
    // 4) 모두 실패
    // - 필터 기준(navProductNo, code, name)이 있었다면 빈 배열 반환하여 전체 노출 방지
    // - 아무 기준도 없었다면 전체 노출
    const hasCriteria = (navProductNo != null) || (code && code !== '-') || !!nameToUse;
    return hasCriteria ? [] : data;
  }, [rows, productCode, location?.state?.productName, location?.state?.productNo]);

  // Apply cancellation filter + column filters on top of base filtering
  const uiFiltered = useMemo(() => {
    const base = hideCancelled ? filtered.filter((row) => !isCancelledRow(row)) : filtered;
    const activeKeys = Object.keys(colFilters).filter((k) => (colFilters[k] ?? '').toString().trim() !== '');
    if (activeKeys.length === 0 && (globalFilter ?? '').toString().trim() === '') return base;
    const lowered = Object.fromEntries(activeKeys.map((k) => [k, colFilters[k].toString().toLowerCase()]));
    const gq = (globalFilter ?? '').toString().trim().toLowerCase();
    return base.filter((row) => {
      // column-specific filters
      const colPass = activeKeys.every((k) => {
        const raw = row?.[k];
        if (raw === null || raw === undefined) return false;
        const display = (formatCellValue(k, raw) ?? '').toString().toLowerCase();
        return display.includes(lowered[k]);
      });
      if (!colPass) return false;
      // global filter across visible columns only
      if (!gq) return true;
      return visibleColumns.some((c) => {
        const raw = row?.[c.key];
        if (raw === null || raw === undefined) return false;
        const display = (formatCellValue(c.key, raw) ?? '').toString().toLowerCase();
        return display.includes(gq);
      });
    });
  }, [filtered, hideCancelled, colFilters, globalFilter]);

  // Apply global sort before grouping so both group order and items reflect the sort
  const sortedUiFiltered = useMemo(() => {
    if (!sortKey) return uiFiltered;
    const arr = uiFiltered.slice();
    arr.sort(compareRows);
    return arr;
  }, [uiFiltered, sortKey, sortDir]);

  // Group by 행사단체번호
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of sortedUiFiltered) {
      const key = r?.행사단체번호 != null ? String(r.행사단체번호) : '기타';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    // Preserve insertion order
    return Array.from(map.entries()).map(([groupKey, items]) => ({
      groupKey,
      groupName: items[0]?.행사단체명 || '',
      items,
    }));
  }, [sortedUiFiltered]);

  // AntD Table uses tree data; control default expanded groups
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const toggleGroupExpand = (groupKey) => {
    setExpandedRowKeys((prev) => {
      const key = `group-${groupKey}`;
      const set = new Set(prev);
      if (set.has(key)) set.delete(key); else set.add(key);
      return Array.from(set);
    });
  };

  // Precompute AntD columns and data to avoid calling hooks conditionally inside JSX
  const antdColumns = useMemo(() => {
    const widthOverrides = {
      주문번호: 120,
      예약일: 130,
      판매처: 160,
      예약상태2: 120,
      행사시작일: 130,
      행사종료일시: 130,
      여행객수: 100,
      예약고객명: 200,
      예약상태3: 120,
      상품금액: 120,
      입금액: 120,
      입금상태: 110,
    };
    const defaultByType = { number: 120, date: 130, string: 160 };
    const cols = [
      {
        title: '단체',
        dataIndex: '_group',
        key: '_group',
        width: 220,
        align: 'center',
        ellipsis: false,
        render: (_, record) => {
          if (record._isGroup) {
            const isExpanded = expandedRowKeys.includes(`group-${record.groupKey}`);
            // 높이 일정하게 유지하기 위한 공통 스타일 - 모든 셀 2줄로 통일
            const commonStyle = {
              minHeight: '42px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            };
            
            if (hoveredRowKey === `group-${record.groupKey}`) {
              return (
                <div className="px-5 py-2.5" style={commonStyle} onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="flex flex-col min-w-0 flex-grow">
                      <div>
                        <span
                          className="text-emerald-700 hover:underline cursor-pointer select-none"
                          onClick={() => openGroupDetail(record)}
                        >
                          단체정보 상세보기
                        </span>
                      </div>
                      <div className={`flex items-center ${!(record?._firstChild?.출발확정 || record?._firstChild?.일정확정) ? 'justify-center' : ''} gap-1`}>
                        <span className="text-xs text-gray-700">(주문 <span className="text-red-700 tabular-nums">{record.count}</span>건)</span>
                        {(record?._firstChild?.출발확정 || record?._firstChild?.일정확정) && (
                          <div className="flex gap-0.5 ml-1">
                            {record?._firstChild?.출발확정 && (
                              <span className="inline-flex items-center rounded bg-blue-50 border border-blue-200 px-1 py-0 text-[10px] font-medium text-blue-700 whitespace-nowrap">
                                출발
                              </span>
                            )}
                            {record?._firstChild?.일정확정 && (
                              <span className="inline-flex items-center rounded bg-green-50 border border-green-200 px-1 py-0 text-[10px] font-medium text-green-700 whitespace-nowrap">
                                일정
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="h-0.5"></div> {/* 일관된 높이 유지를 위한 공백 */}
                    </div>
                  </div>
                </div>
              );
            }
            
            return (
              <div className="min-w-0 px-5 py-2.5" style={commonStyle} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex flex-col min-w-0 flex-grow">
                    <div className="text-sm font-medium text-gray-800">
                      단체번호: <span className="text-emerald-700 tabular-nums">{record.groupKey}</span>
                    </div>
                    <div className={`mt-0.5 text-xs text-gray-700 flex items-center justify-center ${!(record?._firstChild?.출발확정 || record?._firstChild?.일정확정) ? 'justify-center' : ''} gap-1`}>
                      <span>(주문 <span className="text-red-700 tabular-nums">{record.count}</span>건)</span>
                      {(record?._firstChild?.출발확정 || record?._firstChild?.일정확정) && (
                        <div className="flex gap-0.5 ml-1">
                          {record?._firstChild?.출발확정 && (
                            <span className="inline-flex items-center rounded bg-blue-50 border border-blue-200 px-1 py-0 text-[10px] font-medium text-blue-700 whitespace-nowrap">
                              출발
                            </span>
                          )}
                          {record?._firstChild?.일정확정 && (
                            <span className="inline-flex items-center rounded bg-green-50 border border-green-200 px-1 py-0 text-[10px] font-medium text-green-700 whitespace-nowrap">
                              일정
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          return <div className="min-h-[42px]" onClick={(e) => e.stopPropagation()} />;
        },
        onHeaderCell: () => ({ style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '10px 20px', backgroundColor: '#f8fafc', fontWeight: 600, textAlign: 'center' } }),
        onCell: () => ({ style: { whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'clip', maxWidth: '100%', padding: 0 } }),
      },
      
      ...visibleColumns.map((c) => ({
        title: (
          <div className="flex flex-col items-stretch gap-1">
            <div className="items-center justify-between gap-2">
              <span className="whitespace-nowrap px-1">{c.label}</span>
              <button type="button" className="text-[11px] px-1.5 py-0.5  rounded hover:bg-gray-100 text-gray-700" title="정렬" onClick={(e) => { e.stopPropagation(); toggleSort(c.key); }}>{sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</button>
            </div>
            <AntInput size="small" placeholder="" value={colFilters[c.key] ?? ''} onChange={(e) => onFilterChange(c.key, e.target.value)} allowClear onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} />
          </div>
        ),
        dataIndex: c.key, key: c.key, align: 'center', width: widthOverrides[c.key] ?? defaultByType[c.type] ?? 140, ellipsis: true,
        onHeaderCell: () => ({ 
          style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '10px 20px', backgroundColor: '#f8fafc', fontWeight: 600, textAlign: 'center' },
          draggable: true,
          onDragStart: (e) => { try { e.dataTransfer.setData('text/plain', c.key); } catch {} setDraggingColKey(c.key); },
          onDragOver: (e) => { e.preventDefault(); },
          onDrop: (e) => { e.preventDefault(); const source = (() => { try { return e.dataTransfer.getData('text/plain') || draggingColKey; } catch { return draggingColKey; } })(); moveColBefore(source, c.key); setDraggingColKey(null); },
        }),
        onCell: () => ({ style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', padding: '10px 20px' } }),
        render: (value, record) => {
          if (record._isGroup) {
            const first = record._firstChild; const v = first ? first[c.key] : undefined;
            const cellStyle = { minHeight: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
            return (
              <div style={cellStyle}>
                <div>
                  <span className={`${c.type === 'number' ? 'tabular-nums' : ''} ${c.type === 'date' ? 'font-mono text-[12.5px]' : ''} ${isCancelledRow(record) ? 'text-red-600 line-through' : ''}`}>{formatCellValue(c.key, v)}</span>
                  <div className="h-4"></div>
                </div>
              </div>
            );
          }
          const cellStyle = { minHeight: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
          return (
            <div style={cellStyle}>
              <div>
                <span className={`${c.type === 'number' ? 'tabular-nums' : ''} ${c.type === 'date' ? 'font-mono text-[12.5px]' : ''} ${isCancelledRow(record) ? 'text-red-600 line-through' : ''}`}>{formatCellValue(c.key, value)}</span>
                <div className="h-4"></div>
              </div>
            </div>
          );
        },
      })),
    ];
    return cols;
  }, [visibleColumns, hoveredRowKey]);

  const totalTableWidth = useMemo(() => {
    return antdColumns.reduce((sum, col) => sum + (col.width || 160), 0);
  }, [antdColumns]);

  const antdData = useMemo(() => {
    // Original tree data (group row + optional children), without hover-outline metadata
    return grouped.map((g) => {
      const firstChild = g.items[0];
      const rest = g.items.slice(1);
      const base = {
        _isGroup: true,
        groupKey: g.groupKey,
        groupName: g.groupName,
        count: g.items.length,
        _firstChild: firstChild,
        _hasChildren: rest.length > 0,
      };
      if (rest.length > 0) {
        return {
          ...base,
          children: rest.map((row, idx) => ({
            ...row,
            key: `${g.groupKey}-${idx + 1}`,
          })),
        };
      }
      return base;
    });
  }, [grouped]);

  // Ensure expanded keys only include groups that actually have children
  useEffect(() => {
    setExpandedRowKeys((prev) => {
      const allowed = new Set(
        antdData
          .filter((r) => r._isGroup && r._hasChildren)
          .map((r) => `group-${r.groupKey}`)
      );
      // If no rows are expanded yet, expand all by default on first load
      if (!prev || prev.length === 0) {
        return Array.from(allowed);
      }
      // Otherwise, keep only valid keys
      return prev.filter((k) => allowed.has(k));
    });
  }, [antdData]);


  // Prefer product name from navigation state, fallback to data
  const navProductName = location?.state?.productName;
  const navProductNo = location?.state?.productNo;
  // Persist name on arrival for refresh/deeplink resiliency
  useEffect(() => {
    if (navProductName && productCode) {
      try {
        sessionStorage.setItem(`order_product_name_${productCode}`, navProductName);
      } catch {}
    }
  }, [navProductName, productCode]);
  // Persist productNo
  useEffect(() => {
    if (navProductNo && productCode) {
      try {
        sessionStorage.setItem(`order_product_no_${productCode}`, String(navProductNo));
      } catch {}
    }
  }, [navProductNo, productCode]);

  // Apply immediate badge updates in the table after saving toggles
  const applyGroupFlagUpdates = (groupNo, updates) => {
    try {
      setRows((prev) =>
        Array.isArray(prev)
          ? prev.map((r) => {
              const key = r?.행사단체번호 != null ? String(r.행사단체번호) : '기타';
              if (String(key) === String(groupNo)) {
                return { ...r, ...updates };
              }
              return r;
            })
          : prev
      );
    } catch {}
  };
  const derivedProductName = useMemo(() => {
    if (navProductName) return navProductName;
    // Check session storage
    try {
      const saved = productCode ? sessionStorage.getItem(`order_product_name_${productCode}`) : null;
      if (saved) return saved;
    } catch {}
    const first = filtered[0];
    if (first?.상품명) return first.상품명;
    // Final fallback to product code to avoid '(알수없음)'
    const fallback = productCode || '';
    // Guard against stray numeric "0" showing up as a title
    return fallback === '0' ? '' : fallback;
  }, [navProductName, filtered, productCode]);

  const derivedProductNo = useMemo(() => {
    if (navProductNo) return navProductNo;
    try {
      const saved = productCode ? sessionStorage.getItem(`order_product_no_${productCode}`) : null;
      if (saved) return Number(saved);
    } catch {}
    // Fallback: try to infer from the first filtered row if available
    try {
      const first = filtered && filtered[0] ? filtered[0] : null;
      if (first) {
        const noKeys = ['상품번호','ProductNo','productNo','판매상품번호'];
        for (const k of noKeys) {
          const v = first?.[k];
          if (v !== undefined && v !== null && String(v).trim() !== '') {
            const num = Number(String(v).trim());
            if (Number.isFinite(num)) return num;
          }
        }
      }
    } catch {}
    return undefined;
  }, [navProductNo, productCode, filtered]);

  // 예약 플로우 상태 (주문내역 → 날짜 선택 → 예약하기)
  const [reserveDateModalOpen, setReserveDateModalOpen] = useState(false);
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedPriceNo, setSelectedPriceNo] = useState('');
  const [selectedProductTypeName, setSelectedProductTypeName] = useState('투어');
  const [modalProductTypeName, setModalProductTypeName] = useState('');

  // money formatting helper (no decimals, with commas)
  const fmtMoney = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const num = Math.trunc(Number(String(v).replace(/,/g, '')));
    if (!Number.isFinite(num)) return String(v);
    return num.toLocaleString('ko-KR');
  };

  // settings modal state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // groupDetail is declared earlier; do not redeclare here
  // additional state for product basic settings modal
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [settingsProduct, setSettingsProduct] = useState(null);

  // Detect product type when opening the reserve date modal (placed after state declarations)
  useEffect(() => {
    let active = true;
    const detectTypeForModal = async () => {
      if (!reserveDateModalOpen) return;
      if (!derivedProductNo) { setModalProductTypeName(''); return; }
      try {
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) { setModalProductTypeName(''); return; }
        let typeName = '투어';
        const details = await getProductDetails(derivedProductNo, companyNo).catch(() => null);
        if (details) {
          const rawType = String(details.상품타입 || details.ProductType || '').trim();
          const rawTypeCode = String(details.상품종류코드 || details.ProductTypeCode || '').trim();
          const rawTypeCode2 = String(details.상품타입코드 || details.ProductTypeCode || '').trim();
          const hotelTypeCodes = new Set(['309001','309002','309003','309004','309005','309006','309007','309008']);
          if (rawType.includes('호텔') || rawTypeCode === '001001' || hotelTypeCodes.has(rawTypeCode2)) {
            typeName = '호텔';
          }
        } else {
          const hotelInfo = await getHotelBasicInfo(derivedProductNo, companyNo).catch(() => null);
          if (hotelInfo) typeName = '호텔';
        }
        if (!active) return;
        setModalProductTypeName(typeName);
      } catch (e) {
        if (!active) return;
        setModalProductTypeName('');
      }
    };
    detectTypeForModal();
    return () => { active = false; };
  }, [reserveDateModalOpen, derivedProductNo]);

  // 기본설정 모달 열기 함수
  const openBasicSettings = async () => {
    try {
      setSettingsLoading(true);
      const { companyNo } = getUserInfo() || {};
      const details = await getProductDetails(derivedProductNo, companyNo);
      if (details) {
        setSelectedProduct(details);
        setIsSettingsOpen(true);
      } else {
        alert('상품 상세 정보를 찾을 수 없습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('상품 상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setSettingsLoading(false);
    }
  };

  // 기본설정 모달 닫기 함수
  const handleCloseModal = () => {
    setIsSettingsOpen(false);
    setSelectedProduct(null);
  };

  // 기본설정 저장 함수
  const handleSaveSettings = async (updatedProduct) => {
    try {
      setSettingsLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      const productNo = updatedProduct.상품번호 || selectedProduct?.상품번호 || derivedProductNo;

      const [cities, managers, productTypes] = await Promise.all([
        getCities().catch(() => []),
        getManagers().catch(() => []),
        getProductTypes().catch(() => []),
      ]);

      const findByName = (list, name) => Array.isArray(list) ? list.find(i => i.코드명 === name) : undefined;
      const original = selectedProduct || {};

      const keywordStr = (updatedProduct.키워드 || '').trim();
      const keywordArr = keywordStr ? keywordStr.split(',').map(k => k.trim()).filter(Boolean) : [];

      const cityName = updatedProduct.도시 ?? original.도시;
      const cityItem = findByName(cities, cityName);
      const cityNo = cityItem?.코드 ?? original.도시번호;

      const productTypeName = updatedProduct.상품타입 ?? original.상품타입;
      const productTypeItem = findByName(productTypes, productTypeName);
      const productTypeCode = productTypeItem?.코드 ?? original.상품타입코드;

      const pdName = updatedProduct.상품담당 ?? original.상품담당;
      const rdName = updatedProduct.예약담당 ?? original.예약담당;
      const pdItem = findByName(managers, pdName);
      const rdItem = findByName(managers, rdName);
      const pdNo = pdItem?.코드 ?? original.상품담당번호;
      const rdNo = rdItem?.코드 ?? original.예약담당번호;

      const basicInfo = {
        ...original,
        상품번호: productNo,
        상품명: updatedProduct.상품명 ?? original.상품명,
        도시번호: cityNo,
        도시: cityName,
        상품타입: productTypeName,
        상품타입코드: productTypeCode,
        상품코드: updatedProduct.상품코드 ?? original.상품코드,
        상품코드룰사용: original.상품코드룰사용 ?? false,
        상품담당: pdName,
        상품담당번호: pdNo,
        예약담당: rdName,
        예약담당번호: rdNo,
        디폴트수배담당: (original.디폴트수배담당 ?? original.예약담당 ?? original.상품담당) || '예약담당',
        소요타입: original.소요타입 ?? original.소요시간타입 ?? original.소요단위 ?? '',
        소요: original.소요 ?? original.소요단위 ?? '',
        소요시간: updatedProduct.소요시간 ?? original.소요시간 ?? 0,
        통화코드: original.통화코드 ?? '',
        통화: original.통화 ?? '',
        가이드타입: original.가이드타입 ?? '',
        가이드타입코드: original.가이드타입코드 ?? '',
        카테고리: original.카테고리 ?? '',
        카테고리코드: original.카테고리코드 ?? '',
        노출여부: typeof updatedProduct.노출여부 === 'boolean' ? updatedProduct.노출여부 : (original.노출여부 ?? false),
        키워드: keywordArr,
        상품뱃지: updatedProduct.상품뱃지 ?? original.상품뱃지,
      };

      await saveProductBasicInfo({
        companyNo,
        userNo,
        productNo,
        basicInfo,
        keywords: keywordArr.join(','),
      });

      alert('성공적으로 저장되었습니다.');
      setIsSettingsOpen(false);
      setSelectedProduct(null);

      return true;
    } catch (e) {
      console.error('Failed to save product details:', e);
      alert('상품 정보 저장에 실패했습니다.');

      return false;
    } finally {
      setSettingsLoading(false);
    }
  };
  return (
    <div className="p-0">
      {!hideHeader && (isQuoteDetailView ? (
        <>
          {/* Header variant for 견적 상세: buttons under title */}
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-lg font-semibold text-gray-800 truncate max-w-[50vw]">{derivedProductName}</span>
                <span className="text-sm text-gray-600 shrink-0">(상품번호: {typeof derivedProductNo !== 'undefined' && derivedProductNo ? derivedProductNo : '-'})</span>
                {headerInlineRight}
                {/* 상품번호가 없는 경우 상품 생성 버튼 표시 */}
                {(!derivedProductNo || derivedProductNo === 0) && onCreateProduct && (
                  <button
                    type="button"
                    className="px-2.5 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    onClick={onCreateProduct}
                    disabled={creatingProduct}
                    title="견적 정보를 기반으로 새 상품을 생성합니다"
                  >
                    {creatingProduct ? (
                      <>
                        <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        생성 중...
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        상품 생성
                      </>
                    )}
                  </button>
                )}
              </div>
              <div className="mt-2 mb-3 flex items-center gap-2">
                <button
                  type="button"
                  className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                  disabled={!derivedProductNo}
                  title={derivedProductNo ? '기본설정' : '상품번호가 없어 기본설정을 열 수 없습니다'}
                  onClick={openBasicSettings}
                >
                  기본설정
                </button>
                <button
                  type="button"
                  className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                  disabled={!derivedProductNo}
                  title={derivedProductNo ? '가격/재고' : '상품번호가 없어 이동할 수 없습니다'}
                  onClick={() => derivedProductNo && navigate(`/price-stock/${derivedProductNo}`)}
                >
                  가격/재고
                </button>
                <button
                  type="button"
                  className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                  disabled={!derivedProductNo}
                  title={derivedProductNo ? '상세 페이지 설정' : '상품번호가 없어 이동할 수 없습니다'}
                  onClick={() => derivedProductNo && navigate(`/detail-settings/${derivedProductNo}`)}
                >
                  상세페이지 설정
                </button>
                
                {typeof onOpenReservationList === 'function' && (
                  <button
                    type="button"
                    className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                    disabled={!derivedProductNo}
                    title={derivedProductNo ? '예약 리스트' : '상품번호가 없어 예약 리스트를 확인할 수 없습니다'}
                    onClick={() => onOpenReservationList()}
                  >
                    예약 리스트
                  </button>
                )}
              </div>
            </div>
            <div className="space-x-2">
              <button
                className="px-3 py-1 text-sm rounded border border-gray-200 hover:bg-gray-50"
                onClick={() => navigate(-1)}
              >
                돌아가기
              </button>
            </div>
          </div>
          <div className="border-b border-gray-200 mb-4" />
          {afterHeader}
        </>
      ) : (
        <>
          {/* Default header */}
          <ProductPageHeader
            productName={derivedProductName}
            productId={derivedProductNo}
            currentPage="orders"
            productType="tour"
            onProductNameClick={() => setSwitcherOpen(true)}
            onBasicSettingsClick={openBasicSettings}
            onBackClick={() => navigate(-1)}
            rightActions={
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all shadow-sm disabled:opacity-50"
                disabled={!derivedProductNo}
                title={derivedProductNo ? '신규 예약' : '상품번호가 없어 신규 예약을 진행할 수 없습니다'}
                onClick={() => setReserveDateModalOpen(true)}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                신규 예약
              </button>
            }
          />
          <ProductSwitcherModal
            open={switcherOpen}
            onClose={() => setSwitcherOpen(false)}
            currentProductId={derivedProductNo}
            onlyTour={true}
            onSelect={(id, row) => { setSwitcherOpen(false); navigate('/orders/-', { state: { productName: row?.상품명, productNo: id } }); }}
          />
          {afterHeader}
        </>
      ))}
      {hideHeader && afterHeader}

    {/* 예약 리스트 섹션 헤더 */}
    {!hideReservationList && isQuoteDetailView && (
      <div className="mb-6 py-4" data-scroll-target="reservation-list">
        <div className="flex items-center justify-between ">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">예약 현황</h3>
              <p className="text-sm text-gray-600">이 견적의 예약 내역을 확인하고 관리할 수 있습니다</p>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Table toolbar: global search, density, column visibility */}
    {!hideReservationList && (
      <div className="mt-4 mb-4 flex flex-wrap items-center gap-2">
        <AntInput
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="전체 검색"
          allowClear
          size="small"
          style={{ width: 240 }}
        />
        <DatePicker.RangePicker
          size="small"
          value={dateRange}
          onChange={(v) => setDateRange(v)}
          allowClear
          placeholder={["시작일", "종료일"]}
        />
        <Checkbox checked={hideCancelled} onChange={(e) => setHideCancelled(e.target.checked)}>취소예약 숨기기</Checkbox>
        <AntButton size="small" type="primary" onClick={loadData}>
          조회
        </AntButton>
      </div>
      )}

      {/* Order list table or empty-state */}
      {!hideReservationList && (antdData.length === 0 ? (
        <div className="mt-6 bg-white border border-gray-200 rounded-lg p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="bg-gray-100 rounded-full p-4 mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">예약 내역이 없습니다</h4>
            <p className="text-gray-600 mb-4">아직 이 상품에 대한 예약이 없습니다.</p>
            <button
              type="button"
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              disabled={!derivedProductNo}
              onClick={() => setReserveDateModalOpen(true)}
            >
              예약 하기
            </button>
          </div>
        </div>
      ) : (
        <div
          className="mt-2 border-y border-gray-200 rounded overflow-visible"
          onMouseLeave={() => setHoveredRowKey(null)}
        >
          <Table
            className="orders-table"
            size={density === 'compact' ? 'small' : 'middle'}
            tableLayout="fixed"
            bordered
            pagination={false}
            scroll={{ x: totalTableWidth }}
            columns={antdColumns}
            dataSource={antdData}
            rowKey={(record) => record.key || (record._isGroup ? `group-${record.groupKey}` : `${record.주문번호 || ''}-${record.예약일 || ''}`)}
            rowClassName={(record) => {
              if (record._isGroup) {
                return 'cursor-pointer';
              } else {
                return 'child-row cursor-pointer';
              }
            }}
            onRow={(record) => ({
              onClick: () => {
                if (record?._isGroup) {
                  // 단체 행 클릭 시 첫 번째 주문의 예약상세로 이동
                  const firstOrder = record._firstChild;
                  if (firstOrder) {
                    openOrderDetail(firstOrder);
                  }
                } else {
                  openOrderDetail(record);
                }
              },
              onMouseEnter: () => {
                const key = record && record._isGroup
                  ? `group-${record.groupKey}`
                  : (record.key || `${record.주문번호 || ''}-${record.예약일 || ''}`);
                setHoveredRowKey(key);
              },
              onMouseLeave: () => setHoveredRowKey(null),
            })}
            expandable={{
              expandedRowKeys,
              // 아코디언 아이콘 숨기기 - 우리는 커스텀 버튼을 사용
              expandIcon: () => null,
              onExpand: (expanded, record) => {
                const key = record && record._isGroup ? `group-${record.groupKey}` : record?.key;
                if (!key) return;
                setExpandedRowKeys((prev) => {
                  const set = new Set(prev);
                  if (expanded) set.add(key); else set.delete(key);
                  return Array.from(set);
                });
              }
            }}
          />
        </div>
      ))}
      <GroupInfoDrawer
        open={!!groupDetail?.saleNo}
        onClose={() => setGroupDetail(null)}
        saleNo={groupDetail?.saleNo}
        title={groupDetail?.groupName}
      />

      {/* 예약 가능 날짜 선택 모달 */}
      <AvailableDatesModal
        open={reserveDateModalOpen}
        onClose={() => setReserveDateModalOpen(false)}
        productNo={derivedProductNo}
        productName={derivedProductName}
        initialProductTypeName={modalProductTypeName}
        onSelect={({ date, datePriceNo, productTypeName }) => {
          setSelectedDate(date);
          setSelectedPriceNo(String(datePriceNo || ''));
          setSelectedProductTypeName(productTypeName || '투어');
          setReserveDateModalOpen(false);
          // 예약 모달 열기
          setReservationModalOpen(true);
        }}
      />

      {/* 예약하기 모달 (기존 공용 컴포넌트 재사용) */}
      {String(selectedProductTypeName || '').trim() === '호텔' ? (
        <HotelReservationModal
          open={reservationModalOpen}
          onClose={() => setReservationModalOpen(false)}
          priceNo={selectedPriceNo}
          datePriceNo={selectedPriceNo}
          date={selectedDate}
          productName={derivedProductName}
        />
      ) : (
        <TourReservationModal
          open={reservationModalOpen}
          onClose={() => setReservationModalOpen(false)}
          priceNo={selectedPriceNo}
          datePriceNo={selectedPriceNo}
          date={selectedDate}
          productName={derivedProductName}
        />
      )}

      {/* 기본설정 모달 */}
      {isSettingsOpen && (
        <ProductSettingsModal
          product={selectedProduct || settingsProduct}
          onClose={handleCloseModal}
          onSave={handleSaveSettings}
          loading={settingsLoading}
        />
      )}
    </div>
  );
}

